import { Getter } from '../Getter';
import { HttpError } from '../HttpError';
import { ApiPullRequestReviewInsight } from './Models/ApiPullRequestReviewInsight';
import { PullRequestReviewInsight } from './Models/PullRequestReviewInsight';
import isValidUUID from 'uuid-validate';

type ApiResponse = ApiPullRequestReviewInsight | null | undefined;

interface DataResponse {
  data?: PullRequestReviewInsight;
  error?: never;
}
interface ErrorResponse {
  data?: never;
  error: HttpError;
}
export type ClientOutput = DataResponse | ErrorResponse;

export class PullRequestInsightClient {
  private getter: Getter<ApiResponse | HttpError>;

  constructor(getter: Getter<ApiResponse | HttpError>) {
    this.getter = getter;
  }

  public async retrieveAsync(organizationUUID: string): Promise<ClientOutput> {
    if (!isValidUUID(organizationUUID)) {
      // equivalent to the response we'd get on the server
      return {
        error: new HttpError(400),
      };
    }

    const response = await this.tryRetrieveAsync(organizationUUID);

    if (!response) {
      return {
        data: undefined,
        error: undefined,
      };
    }

    if (response instanceof HttpError) {
      return {
        error: response,
      };
    }

    if (response.version !== 1) {
      throw new Error(
        `Only version 1 of the '${this.getHref(
          organizationUUID
        )}' payload body is supported`
      );
    }

    return {
      data: this.toPullRequestReviewInsight(response),
    };
  }

  private async tryRetrieveAsync(
    organizationUUID: string
  ): Promise<ApiResponse | HttpError> {
    const href = this.getHref(organizationUUID);

    return await this.getter.get(href);
  }

  private toPullRequestReviewInsight(
    response: ApiPullRequestReviewInsight
  ): PullRequestReviewInsight {
    const { period, time, report } = response;

    return {
      period: {
        type: period.type,
        from: new Date(period.from),
        value: period.value,
      },
      insightCreatedDate: new Date(time),
      numPullRequestReviews: report.pullrequest_reviews ?? 0,
      numInternalReviews: report.internal_reviews ?? 0,
      numTotalReviews: report.total_reviews,
      averagePullRequestIssuesCaughtCount:
        report.pullrequest_issues ?? undefined,
      averageInternalIssuesCaughtCount: report.internal_issues ?? undefined,
      pullRequestIssuesCaughtImpactPercent: report.change_percent ?? undefined,
    };
  }

  private getHref(organizationUUID: string): string {
    return `/api/v1/organizations/${organizationUUID}/benchmark-insight/pull_request_review`;
  }
}
